import type { Draft, DraftRide } from '@trippath/shared';

/** The first of `values` said, else none. */
function first<T>(values: (T | null)[]): T | null {
  return values.find((value) => value !== null) ?? null;
}

/**
 * Several files of one booking read into one draft — an invoice and its boarding pass: a ticket
 * if any is one, its name and day from the one that says its ride; the most paid, in its money,
 * as the invoice may leave a fee out that the boarding pass counts; each other part the first
 * said; its legs those of the file saying the most, each part of each the first said of it.
 */
export function mergeDrafts([one, ...more]: [Draft, ...Draft[]]): Draft {
  const all = [one, ...more];
  const ticket = all.find((draft) => draft.kind === 'ticket');
  const lead = all.find((draft) => (draft.ticket?.legs.length ?? 0) > 0) ?? ticket ?? one;
  const priced = all.reduce<Draft | null>(
    (best, draft) => (draft.price !== null && (best?.price ?? -1) < draft.price ? draft : best),
    null,
  );
  // Its legs those of the file that says the most of them, each part a leg leaves unsaid taken
  // from the same leg in another file — by its ends, else by its place.
  const legs = all
    .map((draft) => draft.ticket?.legs ?? [])
    .reduce((most, these) => (these.length > most.length ? these : most), []);
  const same = (leg: DraftRide, at: number) =>
    all.flatMap((draft) => {
      const theirs = draft.ticket?.legs ?? [];
      return (
        theirs.filter(
          (other) => other.origin === leg.origin && other.destination === leg.destination,
        )[0] ??
        theirs[at] ??
        []
      );
    });
  return {
    ...lead,
    stop: first(all.map((draft) => draft.stop)),
    day: lead.day ?? first(all.map((draft) => draft.day)),
    price: priced?.price ?? null,
    currency: priced?.currency ?? null,
    pay: all.some((draft) => draft.pay === 'paid') ? 'paid' : 'later',
    reference: first(all.map((draft) => draft.reference)),
    link: first(all.map((draft) => draft.link)),
    ticket:
      legs.length === 0
        ? null
        : {
            mode: first(all.map((draft) => draft.ticket?.mode ?? null)),
            legs: legs.map((leg, at) => {
              const said = <K extends keyof DraftRide>(key: K) =>
                first([leg[key], ...same(leg, at).map((other) => other[key])]);
              return {
                ...leg,
                number: said('number'),
                operator: said('operator'),
                vehicle: said('vehicle'),
                platform: said('platform'),
                seat: said('seat'),
                passenger: said('passenger'),
                ticket: said('ticket'),
              };
            }),
          },
  };
}
